import { useState } from 'react';
import validateNewListingForm from '../data/validation/NewListingFormValidation';
import * as API from '../lib/JerseyDirectoryAPI';

const UseUpdateListingForm = (startPlanOption, customerNumber, modelType) => {
    // State to show the update listing form or not
    const [showUpdateListingForm, setShowUpdateListingForm] = useState(false);

    // State to determine if the form has been submitted or not
    const [isSubmitted, setIsSubmitted] = useState(false);

    // State to determine if the form is being processed or not
    const [isProcessing, setIsProcessing] = useState(false);

    // State to determine if the form is successful or not
    const [isSuccessfull, setIsSuccessfull] = useState(false);

    // State for form validation errors
    const [errors, setErrors] = useState({});

    // State for API response messages
    const [apiResponseMessage, setApiResponseMessage] = useState('');

    // Derived states for showing success, errors, and loading
    const showSuccess = isSubmitted && !isProcessing && isSuccessfull;
    const showErrors = isSubmitted && !isProcessing && !isSuccessfull;
    const showLoading = isSubmitted && isProcessing;

    // State for the form data
    const [updateListing, setUpdateListing] = useState({
        ID: '',
        planOption: startPlanOption || '',
        CategoryID: '',
        SubCategoryID: '',
        CustomerNumber: customerNumber || '',
        PositionTitle: '',
        Prefix: '',
        FirstName: '',
        LastName: '',
        OrganizationName: '',
        OwnerName: '',
        Address1: '',
        Address2: '',
        City: '',
        StateProvince: '',
        ZipCode: '',
        CountryID: '',
        LocationDescription: '',
        CowCount: null,
        PerformanceProgram: 'NONE',
        EquityInvestor: false,
        Logo: '',
        InvoiceNumber: 1,
        HomePhone: '',
        WorkPhone: '',
        MobilePhone: '',
        Fax: '',
        Email: '',
        WebsiteUrl: '',
        FacebookUrl: '',
        LinkedInUrl: '',
        YoutubeUrl: '',
        InstagramUrl: '',
        FarmMessage: '',
        LandingSiteType: '',
        AutoReEnroll: true,
        HeiferGrowerOnly: false,
        IsActive: false,
        ModifiedDate: null,
        ContactName1: null,
        ContactType1: null,
        ContactTest1: null,
        ContactName2: null,
        ContactType2: null,
        ContactTest2: null,
        ContactName3: null,
        ContactType3: null,
        ContactTest3: null,
        ContactName4: null,
        ContactType4: null,
        ContactTest4: null,
        Creator: '',
        Picture: null,
        Notes: null,
        QueenOfQuality: false,
        SiteType: null,
        Complimentary: null,
    });

    // Handles the update of the values within the form model
    function handleChange(event) {
        const { name, value, type, checked } = event.target;
        const newValue = type === 'checkbox' ? checked : value;
        setUpdateListing(prevState => ({
            ...prevState,
            [name]: newValue
        }));
    }

    // Handles the update of the values based on the listing plan selected
    const handlePlanChange = (e) => {
        const { value } = e.target;

        if (value === 'B') {
            setUpdateListing((prevData) => ({
                ...prevData,
                planOption: value,
                farmMessage: '',
                landingSiteType: '',
                Picture: null,
                fileName: null,
            }));
        } else if (value === 'I') {
            setUpdateListing((prevData) => ({
                ...prevData,
                planOption: value,
                landingSiteType: '',
            }));
        }
    };

    // Handles the update of the values based on the listing type selected
    const handleListingTypeChange = (event) => {
        const { name, value } = event.target;
        setUpdateListing((prevData) => {
            const updatedData = {
                ...prevData,
                [name]: value,
            };

            if (value === '3' || value === '4') {
                return {
                    ...updatedData,
                    listingType: value,
                    firstName: '',
                    lastName: '',
                };
            } else {
                return {
                    ...updatedData,
                    listingType: value,
                    organizationName: '',
                    ownerName: '',
                };
            }
        });
    };

    // Handles the picture file change event
    const handleFileChange = async (event) => {
        try {
            const file = event.target.files[0];
            if (!file) {
                return;
            }

            // Convert the file to a byte array
            const arrayBuffer = await file.arrayBuffer();
            const byteArray = new Uint8Array(arrayBuffer);

            // Convert byte array to Base64 string
            const base64String = btoa(String.fromCharCode(...byteArray));

            setUpdateListing((prevState) => ({
                ...prevState,
                Picture: base64String,
                fileName: file.name,
            }));
        } catch (error) {
        }
    };

    // Handles the submission and processing of the new listing form
    const handleSubmit = (e) => {
        e.preventDefault();

        // Reset submission state
        setIsSubmitted(false);

        // Validate the form and set the validation errors
        const validationErrors = validateNewListingForm(updateListing);
        setErrors(validationErrors);

        // Check if there are any non-empty error messages
        const hasErrors = Object.values(validationErrors).some(error => error !== '');

        if (!hasErrors) {
            setIsProcessing(true);

            if (modelType === 'directory') {

                API.updateDirectoryEntry(updateListing.ID, updateListing)
                    .then((res) => {
                        if (res && res.status === 200) {
                            setIsSuccessfull(true);
                        } else {
                            setIsSuccessfull(false);
                            setApiResponseMessage(res ? res.data : 'Unknown error');
                        }
                        setIsProcessing(false);
                    })
                    .catch((error) => {
                        setIsSuccessfull(false);
                        setApiResponseMessage(error.response ? error.response.data : error.message);
                        setIsProcessing(false);
                    });
            } else if (modelType === 'pending') {
                API.addDirectoryEntry(updateListing)
                    .then(() => API.deletePendingDirectoryListing(updateListing.ID, updateListing))
                    .then((res) => {
                        if (res && res.status === 200) {
                            setIsSuccessfull(true);
                            setApiResponseMessage('Listing created successfully!');
                        } else {
                            setIsSuccessfull(false);
                            setApiResponseMessage(res ? res.data : 'Unknown error');
                        }
                        setIsProcessing(false);
                    })
                    .catch((error) => {
                        setIsSuccessfull(false);
                        setApiResponseMessage(error.response ? error.response.data : error.message);
                        setIsProcessing(false);
                    });
            }
        } else {
        }

        setIsSubmitted(true);
    };

    return {
        showUpdateListingForm,
        isSubmitted,
        isProcessing,
        isSuccessfull,
        errors,
        apiResponseMessage,
        showSuccess,
        showErrors,
        showLoading,
        updateListing,
        handleChange,
        handlePlanChange,
        handleListingTypeChange,
        handleFileChange,
        handleSubmit,
    };
};

export default UseUpdateListingForm;